/**
 * Helpers shared by the Ready to Ship modal and the page that hosts it.
 */

/**
 * Whether the modal should show its loading placeholder rather than the product.
 *
 * The rule is deliberately not "the store's product does not match the requested
 * id": that condition has no owner, so anything that leaves the two out of sync
 * (a superseded response, a request that fails, an id that changes while a
 * request is in flight) parks the modal on the spinner with nothing able to
 * clear it. The placeholder is instead tied to a request that is actually in
 * flight for the requested id, so it always resolves — either into the product
 * or, if the request failed, into the error state.
 *
 * Ids are compared as strings: the requested id arrives as a string when it
 * comes from the url and as the API's own type when it comes from a variant
 * button.
 */
export const isBestSellerLoading = (product: any, requestedId: any, pendingId?: string): boolean => {
  if (!requestedId) return false;
  if (String(product?.bestseller_id ?? '') === String(requestedId)) return false;
  // Either there is nothing to render yet, or what we have belongs to a
  // different best seller and the right one is still on its way.
  return !product || String(pendingId ?? '') === String(requestedId);
};

const bsDebugEnabled = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).has('bsDebug');
  } catch {
    return false;
  }
};

/**
 * Traces the best seller id as it moves between the url, the page params and the
 * store. Off unless `bsDebug` is present in the query string, so it can be left
 * in place: add `&bsDebug=1` to the builder url to follow a variant switch.
 */
export const bsLog = (label: string, detail?: unknown) => {
  if (!bsDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[bestSeller] ${label}`, detail ?? '');
};
